import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchAdminMetrics,
  fetchAdminParticipants,
  fetchAdminSystemHealth,
  retryAttendeeZohoInvoice,
  retryReservationZohoInvoice,
  syncOrderWithRazorpay,
  verifyAdminKeyApi,
  fetchBooths,
  forceReleaseBooth,
  downloadAttendeesCsv,
  AdminMetrics,
  Booth,
  AdminUser,
  API_BASE,
} from '../lib/api';
import {
  isSupabaseConfigured,
  signInWithEmailPassword,
  signInWithMagicLink,
  signOut as supabaseSignOut,
} from '../lib/supabase';
import {
  ShieldCheck,
  RefreshCw,
  Download,
  LogOut,
  KeyRound,
  AlertCircle,
  AlertTriangle,
  Eye,
  EyeOff,
  Crown,
  Shield,
  ScanLine,
  CheckCircle2,
  Menu,
  X,
  ArrowRight,
  Globe,
  FileText,
  Printer,
  Activity,
} from 'lucide-react';
import { eventInfo } from '../data';
import { AdminNav } from './admin/AdminNav';
import type { AdminContextType } from './admin/adminContext';

export function AdminPage() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  const [adminKey, setAdminKey] = useState<string>(
    () => sessionStorage.getItem('iiab_admin_key') || ''
  );
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(() => {
    try {
      const stored = sessionStorage.getItem('iiab_admin_user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [keyInput, setKeyInput] = useState('');
  const [showKeyPassword, setShowKeyPassword] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isVerifyingKey, setIsVerifyingKey] = useState(false);

  // Supabase Auth states
  const [loginMethod, setLoginMethod] = useState<'secret_key' | 'supabase'>(
    isSupabaseConfigured ? 'supabase' : 'secret_key'
  );
  const [supabaseEmail, setSupabaseEmail] = useState('');
  const [supabasePassword, setSupabasePassword] = useState('');
  const [isMagicLinkSent, setIsMagicLinkSent] = useState(false);
  const [isSupabaseSubmitting, setIsSupabaseSubmitting] = useState(false);

  const [actionSuccessMessage, setActionSuccessMessage] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isRecoveryDrawerOpen, setIsRecoveryDrawerOpen] = useState(false);

  // Role-based automatic landing redirect (Decision D8)
  const routeUserByRole = (user: AdminUser | null, currentPath: string, isInitialLogin = false) => {
    if (!user) return;
    const role: string = user.role as string;
    const isAtDefaultSales =
      currentPath === '/admin' ||
      currentPath === '/admin/' ||
      (isInitialLogin && currentPath === '/admin/sales');

    if (role === 'operations') {
      if (isAtDefaultSales) {
        navigate('/admin/operations', { replace: true });
      }
    } else if (role === 'desk') {
      if (!currentPath.startsWith('/admin/operations')) {
        navigate('/admin/operations', { replace: true });
      }
    } else if (role === 'gate' || role === 'staff') {
      if (isInitialLogin && !currentPath.startsWith('/admin/operations')) {
        navigate('/checkin', { replace: true });
      } else if (!currentPath.startsWith('/admin/operations')) {
        navigate('/admin/operations', { replace: true });
      }
    }
  };

  useEffect(() => {
    if (currentUser) {
      routeUserByRole(currentUser, location.pathname, false);
    }
  }, [currentUser, location.pathname]);

  // Verify session on mount if adminKey is present and user not yet cached
  useEffect(() => {
    if (adminKey && !currentUser) {
      verifyAdminKeyApi(adminKey)
        .then((res) => {
          if (res.user) {
            setCurrentUser(res.user);
            sessionStorage.setItem('iiab_admin_user', JSON.stringify(res.user));
            routeUserByRole(res.user, location.pathname, true);
          }
        })
        .catch(() => {
          // Key was invalid
        });
    }
  }, [adminKey, currentUser]);

  const isOwnerOrSuperAdmin =
    !currentUser || currentUser.role === 'super_admin' || currentUser.role === 'owner';

  // Fetch admin metrics
  const {
    data: metrics,
    isLoading: isMetricsLoading,
    isError: isMetricsError,
    error: metricsError,
    refetch: refetchMetrics,
  } = useQuery<AdminMetrics>({
    queryKey: ['admin-metrics', adminKey],
    queryFn: () => fetchAdminMetrics(adminKey),
    enabled: !!adminKey,
    retry: false,
    refetchInterval: 10000,
  });

  // Fetch all booths for the management table
  const {
    data: booths = [],
    isLoading: isBoothsLoading,
    refetch: refetchBooths,
  } = useQuery<Booth[]>({
    queryKey: ['booths'],
    queryFn: () => fetchBooths(),
    enabled: !!adminKey,
    refetchInterval: 10000,
  });

  // Fetch participants / attendees list for AdminContext
  const {
    data: participantsResponse,
    refetch: refetchAttendees,
  } = useQuery({
    queryKey: ['adminParticipants', 'all', 'all', '', adminKey],
    queryFn: () => fetchAdminParticipants(adminKey, {}),
    enabled: !!adminKey && isOwnerOrSuperAdmin,
    refetchInterval: 15000,
  });

  // Poll System Health & Exception Alerts for owner / super_admin (Decision D9)
  const {
    data: systemHealth,
    refetch: refetchSystemHealth,
    isFetching: isHealthFetching,
  } = useQuery({
    queryKey: ['admin-system-health', adminKey],
    queryFn: () => fetchAdminSystemHealth(adminKey),
    enabled: !!adminKey && isOwnerOrSuperAdmin,
    refetchInterval: 15000,
  });

  // Force release mutation
  const forceReleaseMutation = useMutation({
    mutationFn: (boothId: string) => forceReleaseBooth(boothId, adminKey),
    onSuccess: (data) => {
      setActionSuccessMessage(data.message || `Stall ${data.booth.id} force released`);
      queryClient.invalidateQueries({ queryKey: ['admin-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['booths'] });
      setTimeout(() => setActionSuccessMessage(null), 4000);
    },
    onError: (err: any) => {
      alert(err.message || 'Failed to force release stall');
    },
  });

  // Quick recovery mutations inside System Exception Drawer
  const retryAttendeeInvoiceMutation = useMutation({
    mutationFn: (attendeeId: string) => retryAttendeeZohoInvoice(adminKey, attendeeId),
    onSuccess: (res: any) => {
      setActionSuccessMessage(res?.message || 'Zoho GST Invoice generated for delegate.');
      refetchSystemHealth();
      refetchMetrics();
      refetchAttendees();
      setTimeout(() => setActionSuccessMessage(null), 4000);
    },
    onError: (err: any) => {
      alert(err?.message || 'Failed to generate Zoho GST invoice for delegate.');
    },
  });

  const retryReservationInvoiceMutation = useMutation({
    mutationFn: (reservationId: string) => retryReservationZohoInvoice(adminKey, reservationId),
    onSuccess: (res: any) => {
      setActionSuccessMessage(res?.message || 'Zoho GST Invoice generated for stall reservation.');
      refetchSystemHealth();
      refetchMetrics();
      queryClient.invalidateQueries({ queryKey: ['admin-reservations'] });
      setTimeout(() => setActionSuccessMessage(null), 4000);
    },
    onError: (err: any) => {
      alert(err?.message || 'Failed to generate Zoho GST invoice for stall reservation.');
    },
  });

  const syncRazorpayMutation = useMutation({
    mutationFn: (orderId: string) => syncOrderWithRazorpay(adminKey, orderId),
    onSuccess: (res: any) => {
      setActionSuccessMessage(res?.message || 'Order synchronized with Razorpay.');
      refetchSystemHealth();
      refetchMetrics();
      setTimeout(() => setActionSuccessMessage(null), 4000);
    },
    onError: (err: any) => {
      alert(err?.message || 'Failed to sync order with Razorpay.');
    },
  });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!keyInput.trim() || isVerifyingKey) return;

    setAuthError(null);
    const key = keyInput.trim();
    setIsVerifyingKey(true);
    try {
      const res = await verifyAdminKeyApi(key);
      sessionStorage.setItem('iiab_admin_key', key);
      if (res.user) {
        sessionStorage.setItem('iiab_admin_user', JSON.stringify(res.user));
        setCurrentUser(res.user);
        routeUserByRole(res.user, location.pathname, true);
      }
      setAdminKey(key);
    } catch (err: any) {
      setAuthError(err.message || 'Invalid administrative authorization key. Access denied.');
    } finally {
      setIsVerifyingKey(false);
    }
  };

  const handleSupabasePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabaseEmail.trim() || !supabasePassword.trim() || isSupabaseSubmitting) return;

    setAuthError(null);
    setIsSupabaseSubmitting(true);
    try {
      const { session, error } = await signInWithEmailPassword(supabaseEmail.trim(), supabasePassword);
      if (error || !session) {
        throw new Error(error || 'Failed to sign in with Supabase.');
      }
      const token = session.access_token;
      const res = await verifyAdminKeyApi(token);
      sessionStorage.setItem('iiab_admin_key', token);
      if (res.user) {
        sessionStorage.setItem('iiab_admin_user', JSON.stringify(res.user));
        setCurrentUser(res.user);
        routeUserByRole(res.user, location.pathname, true);
      }
      setAdminKey(token);
    } catch (err: any) {
      setAuthError(
        err.message || 'Supabase authentication failed. Please check your credentials or access permissions.'
      );
    } finally {
      setIsSupabaseSubmitting(false);
    }
  };

  const handleSupabaseMagicLink = async () => {
    if (!supabaseEmail.trim()) {
      setAuthError('Please enter your email address to receive a magic link.');
      return;
    }
    setAuthError(null);
    setIsSupabaseSubmitting(true);
    try {
      const { error } = await signInWithMagicLink(supabaseEmail.trim());
      if (error) throw new Error(error);
      setIsMagicLinkSent(true);
    } catch (err: any) {
      setAuthError(err.message || 'Failed to send magic link.');
    } finally {
      setIsSupabaseSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await supabaseSignOut();
    sessionStorage.removeItem('iiab_admin_key');
    sessionStorage.removeItem('iiab_admin_user');
    setAdminKey('');
    setCurrentUser(null);
    setKeyInput('');
    setAuthError(null);
  };

  const handleExportCsv = async () => {
    if (!adminKey) return;
    try {
      setIsExporting(true);
      await downloadAttendeesCsv(adminKey);
    } catch (err: any) {
      alert(err.message || 'Failed to download attendee roster CSV');
    } finally {
      setIsExporting(false);
    }
  };

  // If user entered key and query returned error: ONLY deny access if explicitly 401 Unauthorized
  if (adminKey && isMetricsError) {
    const err = metricsError as any;
    const isAuthDenied = err?.status === 401 || err?.statusCode === 401;

    if (isAuthDenied) {
      return (
        <div className="min-h-screen bg-[#F8F9F6] flex items-center justify-center p-4">
          <div className="max-w-md w-full p-6 bg-white border border-rose-200 rounded-3xl shadow-xl text-center space-y-4">
            <div className="w-12 h-12 mx-auto rounded-full bg-rose-50 text-rose-600 border border-rose-200 flex items-center justify-center">
              <AlertCircle className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Administrative Access Denied</h3>
            <p className="text-sm text-slate-600">
              {err?.message || 'The administrative secret key provided was rejected.'}
            </p>
            <button
              onClick={handleLogout}
              className="w-full py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 rounded-xl text-sm font-semibold transition-colors cursor-pointer"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }
  }

  // Unified Staff & Operations Console Login Screen (Decision D8)
  if (!adminKey) {
    return (
      <div className="min-h-screen bg-[#062B1B] flex flex-col justify-between p-4 sm:p-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(197,160,89,0.14),_transparent_55%)] pointer-events-none" />

        {/* Top bar back to public site */}
        <div className="max-w-6xl w-full mx-auto flex items-center justify-between relative z-10">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="/logo/iiab_logo.png"
              alt="IIAB 2027 Logo"
              className="h-9 w-auto object-contain bg-white/95 p-1 rounded-xl"
            />
            <div>
              <span className="text-sm font-black tracking-tight text-white block">IIAB 2027</span>
              <span className="text-[11px] text-emerald-300">{eventInfo.venue.shortName}, Mumbai</span>
            </div>
          </Link>

          <Link
            to="/"
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-emerald-100 text-xs font-bold transition-colors border border-white/15"
          >
            <Globe className="w-3.5 h-3.5 text-[#E6C687]" />
            <span>Back to Public Website</span>
          </Link>
        </div>

        {/* Center Unified Login Card */}
        <div className="max-w-md w-full mx-auto my-8 p-7 sm:p-8 bg-white border border-slate-200/90 rounded-3xl shadow-2xl space-y-6 relative z-10">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0A3D27]/10 border border-[#0A3D27]/20 text-[#0A3D27] text-[11px] font-bold">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>Unified Staff &amp; Operations Console</span>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-tr from-[#0A3D27] to-[#062B1B] text-white flex items-center justify-center shadow-md shadow-[#0A3D27]/20 shrink-0">
                <KeyRound className="w-5 h-5 text-[#E6C687]" />
              </div>
              <div>
                <h1 className="text-xl font-black text-slate-900">Staff &amp; Operations Login</h1>
                <p className="text-xs text-slate-500">
                  Single sign-on for Super Admin, Owner, Operations, Helpdesk &amp; Gate Staff
                </p>
              </div>
            </div>
          </div>

          {/* Supported Roles Pill Bar */}
          <div className="flex items-center gap-1.5 flex-wrap bg-slate-50 p-2.5 rounded-2xl border border-slate-200/80 text-[10px] font-bold text-slate-600">
            <span className="px-2 py-0.5 rounded-md bg-amber-50 text-amber-800 border border-amber-200">
              Super Admin
            </span>
            <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200">
              Owner
            </span>
            <span className="px-2 py-0.5 rounded-md bg-teal-50 text-teal-800 border border-teal-200">
              Operations
            </span>
            <span className="px-2 py-0.5 rounded-md bg-purple-50 text-purple-800 border border-purple-200">
              Helpdesk
            </span>
            <span className="px-2 py-0.5 rounded-md bg-sky-50 text-sky-800 border border-sky-200">
              Gate Scanner
            </span>
          </div>

          {/* Method Switcher Tabs (if Supabase is configured) */}
          {isSupabaseConfigured && (
            <div className="flex rounded-xl bg-slate-100 p-1 border border-slate-200 text-xs font-bold">
              <button
                type="button"
                onClick={() => {
                  setLoginMethod('supabase');
                  setAuthError(null);
                }}
                className={`flex-1 py-1.5 rounded-lg transition cursor-pointer ${
                  loginMethod === 'supabase'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                Staff Email Login
              </button>
              <button
                type="button"
                onClick={() => {
                  setLoginMethod('secret_key');
                  setAuthError(null);
                }}
                className={`flex-1 py-1.5 rounded-lg transition cursor-pointer ${
                  loginMethod === 'secret_key'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                Authorization Key
              </button>
            </div>
          )}

          {authError && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{authError}</span>
            </div>
          )}

          {loginMethod === 'supabase' && isSupabaseConfigured ? (
            <form onSubmit={handleSupabasePasswordLogin} className="space-y-4">
              {isMagicLinkSent ? (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 space-y-2 text-center">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 mx-auto" />
                  <div className="font-bold">Magic Link Dispatched</div>
                  <p>
                    Check your inbox at <strong>{supabaseEmail}</strong> and click the link to enter.
                  </p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                      Staff Email Address
                    </label>
                    <input
                      type="email"
                      required
                      placeholder="name@company.com"
                      value={supabaseEmail}
                      onChange={(e) => setSupabaseEmail(e.target.value)}
                      className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27] focus:ring-1 focus:ring-[#0A3D27]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showKeyPassword ? 'text' : 'password'}
                        required
                        placeholder="Enter password"
                        value={supabasePassword}
                        onChange={(e) => setSupabasePassword(e.target.value)}
                        className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27] focus:ring-1 focus:ring-[#0A3D27] pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowKeyPassword(!showKeyPassword)}
                        className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 cursor-pointer"
                      >
                        {showKeyPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-1">
                    <button
                      type="button"
                      onClick={handleSupabaseMagicLink}
                      disabled={isSupabaseSubmitting}
                      className="text-xs text-[#0A3D27] hover:underline font-semibold cursor-pointer"
                    >
                      Email Magic Link instead
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={isSupabaseSubmitting}
                    className="w-full py-3 px-4 bg-[#0A3D27] hover:bg-[#062B1B] text-white font-bold rounded-xl text-sm transition-all shadow-md shadow-[#0A3D27]/20 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSupabaseSubmitting ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Authenticating...</span>
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="w-4 h-4" />
                        <span>Sign In to Console</span>
                      </>
                    )}
                  </button>
                </>
              )}
            </form>
          ) : (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                  Staff / Break-Glass Authorization Key
                </label>
                <div className="relative">
                  <input
                    type={showKeyPassword ? 'text' : 'password'}
                    value={keyInput}
                    onChange={(e) => setKeyInput(e.target.value)}
                    placeholder="Enter authorization key"
                    required
                    className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27] focus:ring-1 focus:ring-[#0A3D27] pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKeyPassword(!showKeyPassword)}
                    className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    {showKeyPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="flex items-center justify-between text-[11px] text-slate-500 mt-2">
                  <span>
                    Verified via <code className="text-[#0A3D27] font-semibold">ADMIN_SECRET_KEY</code>
                  </span>
                  <span className="text-slate-400 truncate max-w-[180px]" title={API_BASE}>
                    API: <code className="text-slate-600">{API_BASE}</code>
                  </span>
                </div>
              </div>

              <button
                type="submit"
                disabled={isVerifyingKey}
                className="w-full py-3 px-4 bg-[#0A3D27] hover:bg-[#062B1B] text-white font-bold rounded-xl text-sm transition-all shadow-md shadow-[#0A3D27]/20 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isVerifyingKey ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Verifying credentials...</span>
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4" />
                    <span>Enter Staff &amp; Operations Console</span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Footer note */}
        <div className="text-center text-[11px] text-emerald-200/70 relative z-10">
          {eventInfo.title} • {eventInfo.dates.display} • {eventInfo.venue.name}
        </div>
      </div>
    );
  }

  const attendeesList =
    (metrics as any)?.attendees?.length
      ? (metrics as any).attendees
      : participantsResponse?.participants || [];

  const healthRaw = systemHealth as any;
  const missingAttendeeInvoicesList: any[] =
    healthRaw?.alerts?.attendeesMissingInvoice || healthRaw?.attendeesMissingInvoice || [];
  const missingReservationInvoicesList: any[] =
    healthRaw?.alerts?.reservationsMissingInvoice || healthRaw?.reservationsMissingInvoice || [];
  const stuckPendingOrdersList: any[] =
    healthRaw?.alerts?.stuckPendingOrders || healthRaw?.stuckPendingOrders || [];

  const totalActiveAlerts =
    healthRaw?.counts?.totalActiveAlerts ??
    missingAttendeeInvoicesList.length + missingReservationInvoicesList.length + stuckPendingOrdersList.length;
  const missingAttendeeInvoicesCount =
    healthRaw?.counts?.attendeesMissingInvoiceCount ??
    healthRaw?.counts?.attendeesMissingInvoice ??
    missingAttendeeInvoicesList.length;
  const missingReservationInvoicesCount =
    healthRaw?.counts?.reservationsMissingInvoiceCount ??
    healthRaw?.counts?.reservationsMissingInvoice ??
    missingReservationInvoicesList.length;
  const stuckPendingOrdersCount =
    healthRaw?.counts?.stuckPendingOrdersCount ??
    healthRaw?.counts?.stuckPendingOrders ??
    stuckPendingOrdersList.length;
  const totalMissingInvoices = missingAttendeeInvoicesCount + missingReservationInvoicesCount;

  const contextValue: AdminContextType = {
    adminKey,
    currentUser,
    metrics,
    booths,
    attendees: attendeesList,
    systemHealth,
    isMetricsLoading,
    isBoothsLoading,
    isMetricsError,
    metricsError,
    refetchMetrics,
    refetchBooths,
    refetchAttendees,
    refetchSystemHealth,
    handleExportCsv,
    isExporting,
    forceReleaseMutation,
    actionSuccessMessage,
    setActionSuccessMessage,
    handleLogout,
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#F8F9F6] text-slate-800">
      {/* Desktop Left Sidebar */}
      <div className="hidden lg:flex lg:shrink-0">
        <AdminNav
          currentUser={currentUser}
          activeAlertsCount={totalActiveAlerts}
        />
      </div>

      {/* Mobile Sidebar Drawer */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden flex">
          <div
            className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs"
            onClick={() => setMobileSidebarOpen(false)}
          />
          <div className="relative z-10 flex w-72 max-w-[85vw] h-full">
            <AdminNav
              currentUser={currentUser}
              activeAlertsCount={totalActiveAlerts}
              onNavigate={() => setMobileSidebarOpen(false)}
            />
          </div>
        </div>
      )}

      {/* Main Full-Viewport Workspace Column */}
      <div className="flex-1 min-w-0 flex flex-col min-h-screen">
        {/* Top System Exception Alert Banner (Decision D9) */}
        {isOwnerOrSuperAdmin && totalActiveAlerts > 0 && (
          <div className="bg-gradient-to-r from-rose-700 via-rose-600 to-amber-600 text-white px-4 sm:px-6 py-2.5 flex items-center justify-between flex-wrap gap-3 shadow-sm">
            <div className="flex items-center gap-2.5 text-xs font-bold">
              <AlertTriangle className="w-4 h-4 text-amber-200 shrink-0 animate-bounce" />
              <span>
                ⚠️{' '}
                {totalMissingInvoices > 0
                  ? `${totalMissingInvoices} Paid Order(s) Missing Zoho GST Invoice`
                  : `${totalActiveAlerts} System Exception(s) Detected`}
                {stuckPendingOrdersCount > 0 && totalMissingInvoices > 0
                  ? ` + ${stuckPendingOrdersCount} Stuck Pending Order(s)`
                  : ''}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsRecoveryDrawerOpen(true)}
                className="px-3 py-1 rounded-lg bg-white text-rose-800 hover:bg-rose-50 text-xs font-black transition-colors cursor-pointer shadow-xs"
              >
                Quick Recover ({totalActiveAlerts})
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin/automations?tab=health')}
                className="px-3 py-1 rounded-lg bg-rose-950/40 hover:bg-rose-950/60 text-white border border-white/25 text-xs font-bold transition-colors flex items-center gap-1 cursor-pointer"
              >
                <span>Review &amp; Recover</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* Compact Top Telemetry & Session Bar */}
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-slate-200 px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => setMobileSidebarOpen(true)}
              className="lg:hidden p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 cursor-pointer"
              aria-label="Open Console Sidebar"
            >
              <Menu className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-2 min-w-0">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
              <span className="text-xs font-black text-slate-900 uppercase tracking-wider truncate">
                IIAB 2027 Operations Workspace
              </span>
              {metrics && (
                <span className="hidden md:inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 bg-slate-100 px-2.5 py-0.5 rounded-full">
                  <span>{metrics.boothsBreakdown?.booked || 0} Stalls Booked</span>
                  <span>•</span>
                  <span>{metrics.attendeesBreakdown?.totalRegistered || 0} Delegates</span>
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => {
                refetchMetrics();
                refetchBooths();
                refetchAttendees();
                if (isOwnerOrSuperAdmin) refetchSystemHealth();
              }}
              disabled={isMetricsLoading || isBoothsLoading || isHealthFetching}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold transition-colors cursor-pointer border border-slate-200"
              title="Refresh Live Telemetry"
            >
              <RefreshCw
                className={`w-3.5 h-3.5 ${
                  isMetricsLoading || isBoothsLoading || isHealthFetching
                    ? 'animate-spin text-[#0A3D27]'
                    : ''
                }`}
              />
              <span className="hidden sm:inline">Refresh</span>
            </button>

            {isOwnerOrSuperAdmin && (
              <button
                onClick={handleExportCsv}
                disabled={isExporting}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer"
              >
                <Download className={`w-3.5 h-3.5 ${isExporting ? 'animate-bounce' : ''}`} />
                <span className="hidden sm:inline">
                  {isExporting ? 'Exporting...' : 'Export CSV'}
                </span>
              </button>
            )}

            {currentUser && (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1 bg-slate-100 border border-slate-200 rounded-xl text-xs">
                {(currentUser.role as string) === 'super_admin' ? (
                  <Crown className="w-3.5 h-3.5 text-amber-600" />
                ) : (currentUser.role as string) === 'owner' ? (
                  <Shield className="w-3.5 h-3.5 text-emerald-600" />
                ) : (currentUser.role as string) === 'desk' ? (
                  <Printer className="w-3.5 h-3.5 text-purple-600" />
                ) : (
                  <ScanLine className="w-3.5 h-3.5 text-sky-600" />
                )}
                <div className="flex flex-col text-left leading-tight">
                  <span className="font-bold text-slate-800 text-[11px]">
                    {currentUser.name || currentUser.email}
                  </span>
                  <span className="text-[9px] text-slate-500 uppercase font-bold">
                    {currentUser.role.replace('_', ' ')}
                  </span>
                </div>
              </div>
            )}

            <button
              onClick={handleLogout}
              title="Sign Out of Console"
              className="p-1.5 bg-slate-100 hover:bg-rose-50 text-slate-500 hover:text-rose-600 rounded-xl transition-colors cursor-pointer border border-slate-200"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* Workspace Content Area */}
        <div className="flex-1 p-4 sm:p-6 space-y-4 max-w-[1600px] w-full mx-auto">
          {/* Resilient Status Alert when metrics server/DB is offline */}
          {isMetricsError && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3.5 flex items-center justify-between gap-4 text-amber-900 shadow-xs">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                <div className="text-xs">
                  <span className="font-bold">Live Database Telemetry Notice:</span>{' '}
                  {metricsError instanceof Error
                    ? metricsError.message
                    : 'Database metrics service unreachable'}
                  .
                </div>
              </div>
              <button
                onClick={() => refetchMetrics()}
                className="px-3 py-1 bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-semibold rounded-lg border border-amber-300 transition-colors cursor-pointer shrink-0"
              >
                Retry
              </button>
            </div>
          )}

          {/* Action Success Alert */}
          {actionSuccessMessage && (
            <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 flex items-center justify-between animate-in fade-in shadow-xs">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#0A3D27]" />
                <span className="font-medium">{actionSuccessMessage}</span>
              </div>
              <button
                onClick={() => setActionSuccessMessage(null)}
                className="text-slate-400 hover:text-slate-700 text-xs cursor-pointer"
              >
                ✕
              </button>
            </div>
          )}

          {/* Dedicated Page Content via Outlet */}
          <Outlet context={contextValue} />
        </div>
      </div>

      {/* 1-Click System Exception Recovery Modal / Drawer */}
      {isRecoveryDrawerOpen && systemHealth && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl max-w-3xl w-full max-h-[85vh] flex flex-col overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="p-5 bg-rose-50 border-b border-rose-200 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <AlertTriangle className="w-5 h-5 text-rose-600" />
                <div>
                  <h3 className="text-sm font-black text-slate-900">
                    System Exception &amp; 1-Click Invoice Recovery Center
                  </h3>
                  <p className="text-xs text-slate-600">
                    Directly regenerate missing Zoho Books GST invoices or sync stuck Razorpay orders.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsRecoveryDrawerOpen(false)}
                className="p-1.5 rounded-xl hover:bg-rose-100 text-slate-500 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto space-y-5 text-xs">
              {/* Missing Attendee Invoices */}
              {missingAttendeeInvoicesList.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-black text-slate-800 uppercase tracking-wider text-[11px]">
                    Paid Delegates Missing Zoho GST Invoice ({missingAttendeeInvoicesList.length})
                  </h4>
                  <div className="divide-y divide-slate-100 border border-slate-200 rounded-2xl overflow-hidden">
                    {missingAttendeeInvoicesList.map((att: any) => (
                      <div
                        key={att.id}
                        className="p-3 bg-white flex items-center justify-between gap-3 flex-wrap"
                      >
                        <div>
                          <div className="font-bold text-slate-900">{att.fullName}</div>
                          <div className="text-[11px] text-slate-500">
                            {att.email} • {att.companyName || 'Individual'} • Tier: {att.tier}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => retryAttendeeInvoiceMutation.mutate(att.id)}
                          disabled={retryAttendeeInvoiceMutation.isPending}
                          className="px-3 py-1.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl font-bold text-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                        >
                          <FileText className="w-3.5 h-3.5" />
                          <span>Regenerate Zoho Invoice</span>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Missing Stall Reservation Invoices */}
              {missingReservationInvoicesList.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-black text-slate-800 uppercase tracking-wider text-[11px]">
                    Booked Stalls Missing Zoho GST Invoice ({missingReservationInvoicesList.length})
                  </h4>
                  <div className="divide-y divide-slate-100 border border-slate-200 rounded-2xl overflow-hidden">
                    {missingReservationInvoicesList.map((res: any) => (
                      <div
                        key={res.id}
                        className="p-3 bg-white flex items-center justify-between gap-3 flex-wrap"
                      >
                        <div>
                          <div className="font-bold text-slate-900">
                            Stall {res.boothId} — {res.companyName}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {res.contactEmail} • ₹{Number(res.agreedAmountInr || 0).toLocaleString('en-IN')}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => retryReservationInvoiceMutation.mutate(res.id)}
                          disabled={retryReservationInvoiceMutation.isPending}
                          className="px-3 py-1.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl font-bold text-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                        >
                          <FileText className="w-3.5 h-3.5" />
                          <span>Regenerate Zoho Invoice</span>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Stuck Pending Orders */}
              {stuckPendingOrdersList.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-black text-slate-800 uppercase tracking-wider text-[11px]">
                    Stuck Pending Razorpay Orders ({stuckPendingOrdersList.length})
                  </h4>
                  <div className="divide-y divide-slate-100 border border-slate-200 rounded-2xl overflow-hidden">
                    {stuckPendingOrdersList.map((ord: any) => (
                      <div
                        key={ord.id}
                        className="p-3 bg-white flex items-center justify-between gap-3 flex-wrap"
                      >
                        <div>
                          <div className="font-bold text-slate-900">
                            Order {ord.razorpayOrderId || ord.id}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {ord.customerEmail || ord.email} • ₹
                            {Number((ord.amountPaise || 0) / 100).toLocaleString('en-IN')}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => syncRazorpayMutation.mutate(ord.id)}
                          disabled={syncRazorpayMutation.isPending}
                          className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-bold text-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                        >
                          <Activity className="w-3.5 h-3.5" />
                          <span>Sync from Razorpay</span>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  setIsRecoveryDrawerOpen(false);
                  navigate('/admin/automations?tab=health');
                }}
                className="text-xs font-bold text-[#0A3D27] hover:underline flex items-center gap-1 cursor-pointer"
              >
                <span>Open Full System Health &amp; Recovery Dashboard</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setIsRecoveryDrawerOpen(false)}
                className="px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-xl text-xs font-bold cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPage;
